import { useEffect } from "react";
import { ArrowLeft, MessageSquare, Brain, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import footerFunding from "@/assets/footer-funding.jpeg";
import { SEOPresets } from "@/components/SEO";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.privacyPolicy}
      <Header showLogout solidBackground={true} />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-2">PRIVACY POLICY</h1>
          <h2 className="text-2xl font-semibold mb-1">
            Sundae Education Technologies SL
          </h2>
          <h3 className="text-xl mb-8">GlobalCoders App</h3>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. INTRODUCTION</h2>
            <p className="mb-4">
              Sundae Education Technologies SL ("Company", "we", "us", or "our")
              is committed to protecting your privacy and personal data. This
              Privacy Policy explains how we collect, use, process, and protect
              your personal information when you use the GlobalCoders mobile
              application and related services ("Service"). This Privacy Policy
              applies to all users of our Service and should be read in
              conjunction with our Terms and Conditions.
            </p>
            <h3 className="text-xl font-semibold mb-3">
              Data Controller Information:
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Company Name: Sundae Education Technologies SL</li>
              <li>
                Registered Address: Carrer de Joan Oliver, 22D, 2-1, Sant Cugat
                del Valles, 08172, Spain
              </li>
              <li>Contact Email: support@globalcoders.app</li>
              <li>Website: globalcoders.app</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              2. LEGAL BASIS FOR PROCESSING
            </h2>
            <p className="mb-4">
              We process your personal data under the following legal bases
              established by the General Data Protection Regulation (GDPR):
            </p>

            <h3 className="text-xl font-semibold mb-3">
              2.1 Contract Performance (Article 6(1)(b) GDPR)
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Account creation and management</li>
              <li>Service delivery and functionality</li>
              <li>Subscription processing and billing</li>
              <li>Customer support</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              2.2 Legitimate Interests (Article 6(1)(f) GDPR)
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Service improvement and development</li>
              <li>Security and fraud prevention</li>
              <li>Analytics and performance monitoring</li>
              <li>Marketing communications (where consent not required)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              2.3 Consent (Article 6(1)(a) GDPR)
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Optional marketing communications</li>
              <li>Non-essential cookies and tracking</li>
              <li>Third-party integrations requiring consent</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              2.4 Legal Obligation (Article 6(1)(c) GDPR)
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Tax and accounting requirements</li>
              <li>Compliance with applicable laws</li>
              <li>Response to legal requests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              3. PERSONAL DATA WE COLLECT
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              3.1 Account Information
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Full name</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Account preferences and settings</li>
              <li>Profile information (optional)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              3.2 Learning and Performance Data
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Interview practice sessions and responses</li>
              <li>Performance metrics and scores</li>
              <li>Progress tracking and learning analytics</li>
              <li>AI-generated feedback and recommendations</li>
              <li>Study patterns and usage statistics</li>
              <li>Skill assessments and competency data</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">3.3 Technical Data</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Device information (model, operating system, version)</li>
              <li>IP address and location data (approximate)</li>
              <li>App usage data and interaction patterns</li>
              <li>Session logs and timestamps</li>
              <li>Crash reports and error logs</li>
              <li>Network information</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              3.4 Communication Data
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Support inquiries and correspondence</li>
              <li>Feedback and survey responses</li>
              <li>Marketing communication preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              3.5 Automatically Collected Data
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Cookies and similar tracking technologies</li>
              <li>Analytics data through third-party services</li>
              <li>Usage patterns and feature utilization</li>
              <li>Performance data and app stability metrics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              4. HOW WE USE YOUR PERSONAL DATA
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              4.1 Service Provision
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Deliver core app functionality and features</li>
              <li>Process interview simulations and provide AI feedback</li>
              <li>Maintain user accounts and preferences</li>
              <li>Enable progress tracking and analytics</li>
              <li>Provide customer support</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              4.2 Service Improvement
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Analyze usage patterns to enhance user experience</li>
              <li>Develop new features and functionality</li>
              <li>Optimize app performance and stability</li>
              <li>Conduct research and development</li>
              <li>Personalize content and recommendations</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              4.3 Business Operations
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Process payments and manage subscriptions</li>
              <li>Prevent fraud and ensure security</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">4.4 Communications</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Send service-related notifications</li>
              <li>Provide customer support</li>
              <li>Share product updates and announcements</li>
              <li>Send marketing communications (with consent)</li>
              <li>Conduct user surveys and feedback collection</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              5. THIRD-PARTY SERVICES AND DATA SHARING
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              5.1 Third-Party Service Providers
            </h3>
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Google Analytics</h4>
              <ul className="list-disc pl-6 mb-3">
                <li>Purpose: App usage analytics and performance monitoring</li>
                <li>
                  Data Shared: Device information, usage patterns, demographic
                  data
                </li>
                <li>
                  Privacy Policy:{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    https://policies.google.com/privacy
                  </a>
                </li>
                <li>Legal Basis: Legitimate interests</li>
              </ul>

              <h4 className="font-semibold mb-2">Payment Processors</h4>
              <ul className="list-disc pl-6 mb-3">
                <li>Purpose: Subscription billing and payment processing</li>
                <li>Data Shared: Payment information, billing details</li>
                <li>Security: PCI DSS compliant processors</li>
                <li>Legal Basis: Contract performance</li>
              </ul>

              <h4 className="font-semibold mb-2">
                Cloud Infrastructure Providers
              </h4>
              <ul className="list-disc pl-6 mb-3">
                <li>Purpose: Data hosting and service delivery</li>
                <li>Data Shared: All service data as necessary</li>
                <li>Security: Enterprise-grade security measures</li>
                <li>Legal Basis: Contract performance</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">
              5.2 Data Sharing Principles
            </h3>
            <p className="mb-2">
              We do not sell, rent, or trade your personal data. We only share
              data:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                With service providers under strict contractual obligations
              </li>
              <li>When required by law or legal process</li>
              <li>To protect our rights or prevent harm</li>
              <li>With your explicit consent</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              5.3 International Data Transfers
            </h3>
            <p className="mb-2">
              Some third-party services may process data outside the EU. We
              ensure adequate protection through:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Adequacy decisions by the European Commission</li>
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>Binding Corporate Rules where applicable</li>
              <li>Certification schemes and codes of conduct</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. DATA RETENTION</h2>

            <h3 className="text-xl font-semibold mb-3">
              6.1 Retention Periods
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Account Data: Retained while account is active plus 3 years
                after closure
              </li>
              <li>
                Learning Data: Retained while account is active plus 1 year for
                service improvement
              </li>
              <li>
                Payment Data: Retained for 7 years for tax and legal compliance
              </li>
              <li>
                Technical Logs: Retained for 12 months for security and
                performance analysis
              </li>
              <li>
                Marketing Data: Retained until consent is withdrawn or 3 years
                of inactivity
              </li>
              <li>
                Support Communications: Retained for 3 years after resolution
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              6.2 Retention Criteria
            </h3>
            <p className="mb-2">Retention periods are based on:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Legal and regulatory requirements</li>
              <li>Business operational needs</li>
              <li>User relationship duration</li>
              <li>Data sensitivity and risk</li>
              <li>Technical limitations</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">6.3 Secure Deletion</h3>
            <p className="mb-4">
              When retention periods expire, we securely delete or anonymize
              personal data using industry-standard methods.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              7. YOUR RIGHTS UNDER GDPR
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              7.1 Right of Access (Article 15)
            </h3>
            <p className="mb-4">
              You can request confirmation of data processing and obtain a copy
              of your personal data.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.2 Right to Rectification (Article 16)
            </h3>
            <p className="mb-4">
              You can request correction of inaccurate or incomplete personal
              data.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.3 Right to Erasure (Article 17)
            </h3>
            <p className="mb-2">
              You can request deletion of your personal data in certain
              circumstances, including:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Data no longer necessary for original purpose</li>
              <li>Withdrawal of consent</li>
              <li>Unlawful processing</li>
              <li>Legal obligation to delete</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              7.4 Right to Restrict Processing (Article 18)
            </h3>
            <p className="mb-4">
              You can request limitation of processing in specific situations.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.5 Right to Data Portability (Article 20)
            </h3>
            <p className="mb-4">
              You can receive your personal data in a structured,
              machine-readable format and transmit it to another controller.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.6 Right to Object (Article 21)
            </h3>
            <p className="mb-4">
              You can object to processing based on legitimate interests or for
              direct marketing purposes.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.7 Rights Related to Automated Decision-Making (Article 22)
            </h3>
            <p className="mb-4">
              You have rights regarding automated decision-making, including
              profiling, though our AI feedback system includes human oversight.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.8 Right to Withdraw Consent
            </h3>
            <p className="mb-4">
              Where processing is based on consent, you can withdraw it at any
              time without affecting prior lawful processing.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              7.9 Exercising Your Rights
            </h3>
            <p className="mb-4">
              To exercise these rights, contact us at support@globalcoders.app.
              We will respond within one month and may request identity
              verification.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. DATA SECURITY</h2>

            <h3 className="text-xl font-semibold mb-3">
              8.1 Technical Measures
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Encryption: Data encrypted in transit and at rest</li>
              <li>
                Access Controls: Role-based access with multi-factor
                authentication
              </li>
              <li>
                Network Security: Firewalls, intrusion detection, and monitoring
              </li>
              <li>Regular Updates: Security patches and system updates</li>
              <li>Backup Systems: Secure, encrypted data backups</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              8.2 Organizational Measures
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Staff Training: Regular privacy and security training</li>
              <li>Access Policies: Strict need-to-know access principles</li>
              <li>Incident Response: Documented breach response procedures</li>
              <li>
                Vendor Management: Due diligence on third-party processors
              </li>
              <li>
                Regular Audits: Internal and external security assessments
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              8.3 Data Breach Notification
            </h3>
            <p className="mb-2">In case of a personal data breach, we will:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Notify supervisory authorities within 72 hours where required
              </li>
              <li>Inform affected users without undue delay if high risk</li>
              <li>Document all breaches and response measures</li>
              <li>Take immediate steps to contain and remedy the breach</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              9. COOKIES AND TRACKING TECHNOLOGIES
            </h2>

            <h3 className="text-xl font-semibold mb-3">9.1 Types of Cookies</h3>

            <h4 className="font-semibold mb-2">Essential Cookies</h4>
            <ul className="list-disc pl-6 mb-3">
              <li>Purpose: Core app functionality and security</li>
              <li>Legal Basis: Legitimate interests</li>
              <li>Duration: Session or up to 1 year</li>
              <li>Opt-out: Not possible (essential for service)</li>
            </ul>

            <h4 className="font-semibold mb-2">Analytics Cookies</h4>
            <ul className="list-disc pl-6 mb-3">
              <li>Purpose: Usage analytics and performance monitoring</li>
              <li>Legal Basis: Consent</li>
              <li>Duration: Up to 2 years</li>
              <li>Opt-out: Available through cookie settings</li>
            </ul>

            <h4 className="font-semibold mb-2">Marketing Cookies</h4>
            <ul className="list-disc pl-6 mb-3">
              <li>Purpose: Personalized advertising and marketing</li>
              <li>Legal Basis: Consent</li>
              <li>Duration: Up to 1 year</li>
              <li>Opt-out: Available through cookie settings</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              9.2 Cookie Management
            </h3>
            <p className="mb-2">You can manage cookie preferences through:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>App settings menu</li>
              <li>Device browser settings</li>
              <li>Third-party opt-out tools</li>
              <li>Direct contact with us</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              9.3 Third-Party Tracking
            </h3>
            <p className="mb-4">
              We use Google Analytics and Amplitude for analytics. These
              services may use cookies and similar technologies. Refer to our
              separate Cookie Policy for detailed information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              10. CHILDREN'S PRIVACY
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              10.1 Age Restrictions
            </h3>
            <p className="mb-4">
              Our Service is designed for users aged 16 and older. We do not
              knowingly collect personal data from children under 16 without
              appropriate consent.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              10.2 Parental Consent
            </h3>
            <p className="mb-4">
              For users under 18 in jurisdictions requiring parental consent, we
              implement appropriate verification measures.
            </p>

            <h3 className="text-xl font-semibold mb-3">10.3 Data Discovery</h3>
            <p className="mb-4">
              If we discover we have collected data from a child without proper
              consent, we will delete it promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              11. PRIVACY BY DESIGN
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              11.1 Data Minimization
            </h3>
            <p className="mb-4">
              We collect only personal data necessary for specified purposes and
              limit processing to what is adequate and relevant.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              11.2 Purpose Limitation
            </h3>
            <p className="mb-4">
              Personal data is processed only for the purposes for which it was
              collected, unless compatible with the original purpose.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              11.3 Storage Limitation
            </h3>
            <p className="mb-4">
              We retain personal data only as long as necessary for the
              specified purposes or as required by law.
            </p>

            <h3 className="text-xl font-semibold mb-3">11.4 Transparency</h3>
            <p className="mb-4">
              We provide clear, accessible information about our data processing
              activities through this Privacy Policy and other communications.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              12. SUPERVISORY AUTHORITY
            </h2>
            <p className="mb-4">
              You have the right to lodge a complaint with a supervisory
              authority, particularly in your country of residence, workplace,
              or where an alleged infringement occurred.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              Spanish Data Protection Authority (AEPD)
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Website:{" "}
                <a
                  href="https://www.aepd.es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  https://www.aepd.es
                </a>
              </li>
              <li>Address: C/ Jorge Juan, 6, 28001 Madrid, Spain</li>
              <li>Phone: +34 901 100 099</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              13. UPDATES TO THIS PRIVACY POLICY
            </h2>

            <h3 className="text-xl font-semibold mb-3">13.1 Policy Changes</h3>
            <p className="mb-4">
              We may update this Privacy Policy to reflect changes in our
              practices, legal requirements, or service features.
            </p>

            <h3 className="text-xl font-semibold mb-3">13.2 Notification</h3>
            <p className="mb-2">
              Material changes will be communicated through:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Email notification to registered users</li>
              <li>In-app notifications</li>
              <li>Website posting with effective date</li>
              <li>Push notifications where appropriate</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">13.3 Continued Use</h3>
            <p className="mb-4">
              Continued use of the Service after changes constitutes acceptance
              of the updated Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              14. CONTACT INFORMATION
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              14.1 General Privacy Inquiries
            </h3>
            <ul className="list-none mb-4">
              <li>Email: support@globalcoders.app</li>
              <li>Subject Line: Privacy Inquiry</li>
              <li>Response Time: Within 5 business days</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              14.2 Data Protection Officer
            </h3>
            <p className="mb-2">
              For complex privacy matters or GDPR-related requests:
            </p>
            <ul className="list-none mb-4">
              <li>Email: support@globalcoders.app</li>
              <li>Subject Line: DPO - Data Protection Request</li>
              <li>Response Time: Within 30 days as required by GDPR</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              14.3 Company Information
            </h3>
            <ul className="list-none mb-4">
              <li>Sundae Education Technologies SL</li>
              <li>Carrer de Joan Oliver, 22D, 2-1</li>
              <li>Sant Cugat del Valles, 08172, Spain</li>
              <li>Website: globalcoders.app</li>
            </ul>

            <p className="text-sm text-muted-foreground mt-8">
              Last Updated: 21st October 2025
            </p>
          </section>
        </div>
      </div>

      {/* Professional Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-primary mt-20">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About section */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Global Coders
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                AI-powered English conversation practice for tech professionals.
                Build confidence, improve communication, and ace your next
                conversation.
              </p>
            </div>

            {/* Quick Links */}

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/subscription-billing"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Subscription and Billing Terms
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Get in Touch
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Have questions or feedback? We'd love to hear from you. Contact
                us on support@globalcoders.app - we do respond to all emails!
              </p>
              <div className="flex gap-4">
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <MessageSquare className="w-5 h-5 text-white" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
                  aria-label="Email"
                >
                  <Brain className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>
          </div>

          {/* Funding section */}
          <div className="border-t border-white/10 pt-8 pb-8">
            <div className="flex justify-center items-center mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl">
                <img
                  src={footerFunding}
                  alt="Financiado por la Unión Europea - NextGenerationEU. Con el apoyo de la Generalitat de Catalunya"
                  className="max-w-full h-auto"
                  style={{ maxHeight: "140px" }}
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                © {new Date().getFullYear()} Global Coders. All rights
                reserved.
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <span>Made with</span>
                <span className="text-white/80">❤️</span>
                <span>for developers worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
